import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Globe, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scoreColor = (s) => (s >= 2 ? '#10B981' : s <= -2 ? '#EF4444' : '#EAB308');

const TrendIcon = ({ trend, score }) => {
  const up = score > 0.5, down = score < -0.5;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  return <Icon size={13} style={{ color: scoreColor(score) }} />;
};

const HistoryChart = ({ history }) => {
  if (!history || history.length < 2) {
    return <p className="text-gray-600 text-xs">History builds one point per day — chart appears as data accumulates ({history?.length || 0}/90 days so far).</p>;
  }
  const w = 600, h = 90;
  const pts = history.map((d, i) => {
    const x = (i / (history.length - 1)) * w;
    const y = h / 2 - (d.score / 10) * (h / 2 - 6);
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20" data-testid="macro-history-chart">
      <line x1="0" y1={h / 2} x2={w} y2={h / 2} stroke="rgba(255,255,255,0.12)" strokeWidth="1" strokeDasharray="4 4" />
      <polyline points={pts} fill="none" stroke="#C5A059" strokeWidth="2" />
    </svg>
  );
};

export const MacroPulse = () => {
  const [pulse, setPulse] = useState(null);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/macro/pulse`).then((r) => setPulse(r.data)).catch(() => setError(true));
    axios.get(`${API}/macro/pulse/history`).then((r) => setHistory(r.data.history)).catch(() => {});
  }, []);

  if (error) {
    return (
      <div className="bg-[#111111] border border-white/10 rounded-xl p-5 mb-6" data-testid="macro-pulse-unavailable">
        <p className="text-gray-500 text-sm">Macro Pulse: data unavailable right now — refusing to show stale or estimated scores.</p>
      </div>
    );
  }
  if (!pulse) return null;

  const color = scoreColor(pulse.score);
  const pct = ((pulse.score + 10) / 20) * 100;

  return (
    <div className="bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-[#C5A059]/25 rounded-xl overflow-hidden mb-8" data-testid="macro-pulse-card">
      <div className="px-6 py-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-[#C5A059]" />
            <h2 className="font-manrope font-bold text-lg text-white">Macro Pulse</h2>
            <span className="text-gray-600 text-[10px] uppercase tracking-widest">Macro bias layer · confidence modifier, never a trade trigger</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-manrope font-extrabold text-2xl" style={{ color }} data-testid="macro-pulse-score">{pulse.score > 0 ? '+' : ''}{pulse.score}</span>
            <span className="px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-wider" style={{ color, borderColor: `${color}55`, background: `${color}12` }}>
              {pulse.regime}
            </span>
          </div>
        </div>

        {/* Dial: -10 .. +10 */}
        <div className="relative h-3 rounded-full overflow-hidden mb-1" style={{ background: 'linear-gradient(to right, rgba(239,68,68,0.55), rgba(234,179,8,0.4), rgba(16,185,129,0.55))' }}>
          <div className="absolute top-0 bottom-0 w-1 bg-white rounded-full shadow-[0_0_8px_rgba(255,255,255,0.9)] transition-all duration-1000" style={{ left: `calc(${pct}% - 2px)` }} />
        </div>
        <div className="flex justify-between text-[10px] text-gray-600 font-bold mb-4"><span>-10 BEARISH</span><span>0</span><span>+10 BULLISH</span></div>

        {/* 90d history */}
        <HistoryChart history={history} />

        {/* Glass Box toggle */}
        <button onClick={() => setOpen(!open)} className="mt-3 flex items-center gap-1.5 text-[#C5A059] text-xs font-bold uppercase tracking-wider hover:text-white transition-colors" data-testid="macro-glassbox-toggle">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Glass Box: factor breakdown
        </button>

        {open && (
          <div className="mt-4 space-y-3" data-testid="macro-glassbox-panel">
            {pulse.factors.map((f) => (
              <div key={f.key} className="bg-black/30 border border-white/5 rounded-lg p-3.5" data-testid={`macro-factor-${f.key}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <span className="flex items-center gap-2 text-white text-sm font-semibold">
                    <TrendIcon trend={f.trend} score={f.score} /> {f.label}
                    <span className="text-gray-500 font-normal">{f.value} · {f.trend}</span>
                  </span>
                  <span className="font-mono text-sm font-bold" style={{ color: scoreColor(f.score) }}>
                    {f.contribution > 0 ? '+' : ''}{f.contribution} pts <span className="text-gray-600 font-normal">({f.weight_pct}%)</span>
                  </span>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed">{f.explain}</p>
                <p className="text-gray-600 text-[10px] mt-1 flex items-center gap-1"><Clock size={10} /> {f.source} · data as of {f.as_of}</p>
              </div>
            ))}
            {pulse.unavailable.map((u) => (
              <div key={u.key} className="bg-black/30 border border-white/5 rounded-lg p-3 text-gray-600 text-xs">
                {u.label}: data unavailable — excluded from score (weights renormalized)
              </div>
            ))}
            <p className="text-gray-600 text-[10px]">{pulse.methodology} Refreshed {new Date(pulse.generated_at).toLocaleString()}.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MacroPulse;
